import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CexAccount } from '../constants';
import { API_BASE_URL } from '../config/api';

// ── Wallet account type ──────────────────────────────────────────────────────
export interface WalletAccount {
  id: number;
  network_name: string;
  network_display_name: string;
  account_label: string;
  wallet_address: string;
  full_display_name: string;
  is_active: boolean;
  last_synced_at: string | null;
  created_at: string;
  // helpers used by the switcher
  cex_display_name: string; // = network_display_name
  account_label_display: string; // = account_label
}

interface CexAccountContextType {
  selectedAccount: CexAccount | null;
  setSelectedAccount: (account: CexAccount | null) => void;
  accounts: CexAccount[];
  fetchAccounts: () => Promise<CexAccount[] | undefined>;
  isLoading: boolean;
  showManualWizard: boolean;
  setShowManualWizard: (v: boolean) => void;
  showDexWizard: boolean;
  setShowDexWizard: (v: boolean) => void;
  // Wallet
  walletAccounts: WalletAccount[];
  fetchWalletAccounts: () => Promise<WalletAccount[] | undefined>;
  isWalletLoading: boolean;
  // Selected wallet account (separate from CEX selected account)
  selectedWalletAccount: WalletAccount | null;
  setSelectedWalletAccount: (w: WalletAccount | null) => void;
}

const CexAccountContext = createContext<CexAccountContextType | undefined>(undefined);

interface CexAccountProviderProps {
  children: ReactNode;
}

export const ALL_ACCOUNTS_ID = -1;

export const ALL_ACCOUNTS_OBJECT: CexAccount = {
  id: ALL_ACCOUNTS_ID,
  cex_name: 'all',
  cex_display_name: 'All Accounts',
  account_label: 'Unified View',
  full_display_name: 'All Accounts - Unified View',
  is_active: true,
  last_synced_at: null,
  created_at: new Date().toISOString(),
};

export const CexAccountProvider: React.FC<CexAccountProviderProps> = ({ children }) => {
  const [selectedAccount, setSelectedAccountState] = useState<CexAccount | null>(null);
  const [accounts, setAccounts] = useState<CexAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showManualWizard, setShowManualWizard] = useState(false);
  const [showDexWizard, setShowDexWizard] = useState(false);
  const [walletAccounts, setWalletAccounts] = useState<WalletAccount[]>([]);
  const [isWalletLoading, setIsWalletLoading] = useState(false);
  const [selectedWalletAccount, setSelectedWalletAccountState] = useState<WalletAccount | null>(null);

  const fetchAccounts = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/cex-accounts`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
        },
      });

      if (response.status === 401) {
        console.warn('Unauthorized. Clearing token...');
        localStorage.removeItem('auth_token');
        const publicPaths = ['/', '/pricing', '/register', '/login', '/about', '/contact', '/privacy', '/terms', '/faq', '/changelog', '/verify-email'];
        const path = window.location.pathname.replace(/\/$/, '') || '/';
        const isPublic = publicPaths.includes(path);
        if (!isPublic) {
          console.warn('Redirecting to login/register from private route...');
          window.location.href = '/register';
        }
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch accounts');
      }

      const data = await response.json();
      if (data.success) {
        console.log('📋 CexAccountContext: Fetched accounts:', data.data);
        setAccounts(data.data);

        // Restore selected account from localStorage
        const savedId = localStorage.getItem('selectedAccountId');
        if (savedId) {
          if (parseInt(savedId) === ALL_ACCOUNTS_ID) {
            console.log('✅ CexAccountContext: Restored All Accounts mode');
            setSelectedAccountState(ALL_ACCOUNTS_OBJECT);
            return;
          }
          const saved = data.data.find((acc: CexAccount) => acc.id === parseInt(savedId));
          if (saved) {
            console.log('✅ CexAccountContext: Restored saved account:', saved.cex_display_name);
            setSelectedAccountState(saved);
            return;
          }
        }

        // Auto-select first account if none selected AND no wallet is saved in localStorage
        const savedWalletId = localStorage.getItem('selectedWalletAccountId');
        if (data.data.length > 0 && !selectedAccount && !savedWalletId) {
          console.log('📌 CexAccountContext: Auto-selecting first account:', data.data[0].cex_display_name);
          setSelectedAccountState(data.data[0]);
          localStorage.setItem('selectedAccountId', data.data[0].id.toString());
        }
        return data.data;
      }
    } catch (error) {
      console.error('Failed to fetch CEX accounts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setSelectedAccount = (account: CexAccount | null) => {
    console.log('🔄 CexAccountContext: Setting selected account:', account?.cex_display_name || 'null');
    setSelectedAccountState(account);
    // When selecting a CEX account, deselect wallet
    setSelectedWalletAccountState(null);
    localStorage.removeItem('selectedWalletAccountId');
    if (account) {
      localStorage.setItem('selectedAccountId', account.id.toString());
    } else {
      localStorage.removeItem('selectedAccountId');
    }
  };

  const setSelectedWalletAccount = (w: WalletAccount | null) => {
    setSelectedWalletAccountState(w);
    // When selecting a wallet, deselect CEX account
    setSelectedAccountState(null);
    localStorage.removeItem('selectedAccountId');
    if (w) {
      localStorage.setItem('selectedWalletAccountId', w.id.toString());
    } else {
      localStorage.removeItem('selectedWalletAccountId');
    }
  };

  // Fetch accounts on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      fetchAccounts();
      fetchWalletAccounts();
    }
  }, []);

  const fetchWalletAccounts = async (): Promise<WalletAccount[] | undefined> => {
    setIsWalletLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      const response = await fetch(`${API_BASE_URL}/api/wallet-accounts`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      });

      if (!response.ok) return;

      const data = await response.json();
      if (data.success) {
        const mapped: WalletAccount[] = (data.data ?? []).map((w: any) => ({
          ...w,
          cex_display_name: w.network_display_name,
          account_label_display: w.account_label,
        }));
        setWalletAccounts(mapped);

        // Restore selected wallet account from localStorage
        const savedWalletId = localStorage.getItem('selectedWalletAccountId');
        if (savedWalletId) {
          const saved = mapped.find((w: WalletAccount) => w.id === parseInt(savedWalletId));
          if (saved) {
            console.log('✅ CexAccountContext: Restored saved wallet account:', saved.full_display_name);
            setSelectedWalletAccountState(saved);
            // Also ensure selected CEX is null
            setSelectedAccountState(null);
          }
        }
        return mapped;
      }
    } catch (err) {
      console.error('Failed to fetch wallet accounts:', err);
    } finally {
      setIsWalletLoading(false);
    }
  };

  return (
    <CexAccountContext.Provider
      value={{
        selectedAccount,
        setSelectedAccount,
        accounts,
        fetchAccounts,
        isLoading,
        showManualWizard,
        setShowManualWizard,
        showDexWizard,
        setShowDexWizard,
        walletAccounts,
        fetchWalletAccounts,
        isWalletLoading,
        selectedWalletAccount,
        setSelectedWalletAccount,
      }}
    >
      {children}
    </CexAccountContext.Provider>
  );
};

export const useCexAccount = () => {
  const context = useContext(CexAccountContext);
  if (!context) {
    throw new Error('useCexAccount must be used within CexAccountProvider');
  }
  return context;
};
